import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { firstValueFrom,map } from 'rxjs';
import {
  PaginatedTransactionResponse,
  TransactionDetailResponse,
  CreateTransferRequest,
  CreateTransferV2Request,
  TransferCreateResponse,
  TransferFlowValidationResult,
  UploadUrlRequest,
  UploadUrlResponse,
} from '../../domain/transaction/transaction-domain';
import { TransactionRepository } from '../../domain/transaction/transaction-repository';
import { BcpEphemeralKeyManager, BcpHttpClient } from '@cix-software-labs/ng-networking';

@Injectable()
export class TransactionHttpRepository extends TransactionRepository {
  private readonly _http = inject(HttpClient);
  private readonly _keyManager = inject(BcpEphemeralKeyManager);
  private readonly _httpClient = inject(BcpHttpClient);

  private get apiUrl(): string { return `${environment.API_URL_BASE}transfers`; }
  private get apiV2Url(): string { return `${this.apiUrl}/v2`; }

  getAll(page: number, pageSize: number): Promise<PaginatedTransactionResponse> {
    const params = new HttpParams().set('page', page.toString()).set('size', pageSize.toString());

    return firstValueFrom(this._http.get<PaginatedTransactionResponse>(this.apiUrl, { params }));
  }

  getById(id: string): Promise<TransactionDetailResponse> {
    return firstValueFrom(this._http.get<TransactionDetailResponse>(`${this.apiUrl}/${id}`));
  }

  public async create(request: CreateTransferRequest): Promise<TransferCreateResponse> {
    await this._keyManager.generate();
    const encryptFields = ['contactGivenNames', 'contactPaternalLastName', 'contactMaternalLastName', 'contactPhoneNumber', 'contactEmail', 'contactDocumentNumber', 'fullContactAddress', 'contactBankAccountType', 'contactBankAccountNumber'];
    return firstValueFrom(
      this._httpClient
        .post<TransferCreateResponse>(this.apiUrl, request, {
          encrypt: encryptFields,
        })
        .pipe(
          map((response) => {
            return response;
          })
        )
    );
  }

  public async createV2(request: CreateTransferV2Request): Promise<TransferCreateResponse> {
    await this._keyManager.generate();
    const encryptFields = [
      'contactGivenNames',
      'contactPaternalLastName',
      'contactMaternalLastName',
      'contactDocumentNumber',
      'contactBankAccountType',
      'contactBankAccountNumber',
    ];
    return firstValueFrom(
      this._httpClient.post<TransferCreateResponse>(this.apiV2Url, request, {
        encrypt: encryptFields,
      })
    );
  }

  generateUploadUrl(request: UploadUrlRequest): Promise<UploadUrlResponse> {
    return firstValueFrom(this._http.post<UploadUrlResponse>(`${this.apiUrl}/upload-url`, request));
  }

  async validateTransferFlow(): Promise<TransferFlowValidationResult> {
    try {
      await firstValueFrom(this._http.post<void>(`${this.apiUrl}/validate`, {}));
      return { isTraditionalFlow: true };
    } catch (_error: unknown) { // NOSONAR: expected — validation failure
      return { isTraditionalFlow: false };
    }
  }
}
